export type GameCategory = "Arcade" | "Word" | "Puzzle" | "Multiplayer" | "Coming soon";

export interface GameRules {
  goal: string;
  controls: string[];
  scoring: string[];
}

export interface GameEntry {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: GameCategory;
  /** Tailwind gradient classes for the cover tile. */
  coverGradient: string;
  /** Emoji or single-glyph icon used as a fallback cover. */
  icon: string;
  /** Public screenshot URLs (none yet — placeholders rendered when empty). */
  screenshots: string[];
  rules: GameRules;
  available: boolean;
  /** Optional full-screen route used by larger games with their own flow. */
  playRoute?: string;
  /** External standalone web-app URL. When set, the game is hosted in an
   * <iframe> (see embed/game-embed.tsx) instead of an in-shell React component.
   * The slumbr Cognito session is bridged into the iframe via postMessage. */
  embedUrl?: string;
}

const ENTRIES: GameEntry[] = [
  {
    id: "riddlr",
    name: "Riddlr",
    tagline: "A different puzzle every day. Everyone gets the same one.",
    description:
      "One puzzle a day, and the kind rotates: word deduction, five-question trivia, anagram scramble, then a word ladder. Everyone on Slumbr gets the same puzzle each day. Play it full screen or drop it straight into a chat with /riddlr — every conversation keeps its own table of who solved it, current streaks and win rates.",
    category: "Word",
    coverGradient: "bg-gradient-to-br from-[#5eead4] via-[#155e63] to-[#08151a]",
    icon: "🧩",
    screenshots: [],
    rules: {
      goal: "Solve today's puzzle, then keep the streak alive against your friends.",
      controls: [
        "Open it here, or type /riddlr in any chat to play it in the thread.",
        "Type on desktop or tap the on-screen keys; trivia is answered by tapping.",
        "Switch to This chat to see everyone's standings.",
      ],
      scoring: [
        "Word: six guesses. Green = right letter, right spot. Amber = right letter, wrong spot.",
        "Trivia and Scramble are out of five — three or better counts as solved.",
        "Ladder: change one letter at a time, every step a real word.",
        "Solve every day to build a streak; miss a day and it resets.",
      ],
    },
    available: true,
    playRoute: "/games/riddlr/play",
  },
  {
    id: "mimic",
    name: "Mimic",
    tagline: "Find the impostors hiding behind almost-right words.",
    description:
      "A social deduction word game for 3-8 players. Civilians share the same secret word, Mimics receive a related word, and everyone gives clues before voting someone out.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#00e5ff] via-[#172033] to-[#ff6a00]",
    icon: "🎭",
    screenshots: [],
    rules: {
      goal: "Civilians vote out every Mimic. Mimics survive by blending in and turning the table against civilians.",
      controls: [
        "Create or join a room while logged into Slumbr.",
        "Use your temporary nickname for in-room fun.",
        "Give a short clue on your turn, then vote during the vote phase.",
      ],
      scoring: [
        "Civilians win when all Mimics are eliminated.",
        "Mimics win when they reach parity with civilians or survive to the final two.",
        "Roles and words are assigned by the Slumbr backend, not by a player host.",
      ],
    },
    available: true,
    playRoute: "/games/mimic/menu",
  },
  {
    id: "blackjack",
    name: "Blackjack",
    tagline: "Beat the dealer to 21 — live, multiplayer.",
    description:
      "Classic casino Blackjack for up to 7 players at one table. Place your bet, get dealt in, then hit, stand, or double down against a live dealer. Blackjack pays 3:2. Real-time over Cloudflare Durable Objects.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#0c2a1a] via-[#0a3d24] to-[#041a0e]",
    icon: "♠",
    screenshots: [],
    rules: {
      goal: "Get closer to 21 than the dealer without going over.",
      controls: [
        "Create or join a table while logged into Slumbr.",
        "Place a bet, then Hit / Stand / Double on your turn.",
        "Dealer hits to 17. Bust over 21 and you lose the bet.",
      ],
      scoring: [
        "Blackjack (A + 10) pays 3:2.",
        "Beat the dealer pays 1:1. Tie pushes (bet back).",
        "Everyone starts with 1,000 chips per table.",
      ],
    },
    available: true,
    playRoute: "/games/blackjack/menu",
  },
  {
    id: "hilo",
    name: "Higher or Lower",
    tagline: "Call the next card — last one standing takes the pot.",
    description:
      "A pure-nerve card gauntlet for 2–8 players. A card flips in the middle; everyone secretly calls the next one Higher or Lower, paying an ante per flip. Miss and you're out of the round — survive everyone and the pot is yours. Ties re-flip free, broke players ride all-in, and a simultaneous wipeout splits the pot. Real-time over Cloudflare Durable Objects.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#6e2338] via-[#521628] to-[#1f0710]",
    icon: "⬆",
    screenshots: [],
    rules: {
      goal: "Outlast everyone by calling each flip correctly; the last player in wins the pot.",
      controls: [
        "Create or join a room while logged into Slumbr.",
        "Each flip: secretly pick Higher or Lower — one ante per pick.",
        "Wrong pick = out of the round. Same rank = free re-flip.",
      ],
      scoring: [
        "Last survivor takes the pot (capped at what they matched; excess rides to the next round).",
        "A simultaneous wipeout of the last group splits the pot.",
        "Out of chips mid-round? You ride all-in for free. Everyone starts with 1,000 chips.",
      ],
    },
    available: true,
    playRoute: "/games/hilo/menu",
  },
  {
    id: "tabletop",
    name: "Card Table",
    tagline: "A shared virtual tabletop — play any card game.",
    description:
      "A shared felt table that remembers: real 52-card decks, chips, dice and spinners you drag around together from any phone, tablet or computer. Cards stay hidden until they're flipped, your hand is truly private, and the table is still there when you come back. No rules enforced — run poker, rummy, go fish, or anything you invent.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#15603f] via-[#0c3d28] to-[#041a0e]",
    icon: "🃏",
    screenshots: [],
    rules: {
      goal: "Free-form: simulate physical cards so you can play any game with friends.",
      controls: [
        "Drag a card to move it · drag the ⋮⋮ tab (or long-press) to move a whole stack.",
        "Double-tap / double-click a stack to flip its top card · long-press or right-click for shuffle and draw.",
        "Pinch or scroll to zoom, two fingers or drag the felt to pan · drag cards into your hand below.",
      ],
      scoring: ["No enforced rules — you run the game yourselves."],
    },
    available: true,
    playRoute: "/games/tabletop/menu",
  },
  {
    id: "zingr",
    name: "Zingr",
    tagline: "Write the funniest answer. Vote for the best.",
    description:
      "A hilarious party game for 3-8 players. Everyone gets witty prompts, writes the funniest answer they can, then the whole room votes head-to-head on the best ones. Two rounds, anonymous answers, and a double-points Last Lash finale. Real-time over Cloudflare Durable Objects.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#ff2fd0] via-[#7c3aed] to-[#1a0b2e]",
    icon: "✦",
    screenshots: [],
    rules: {
      goal: "Score the most points by writing answers the room votes funniest.",
      controls: [
        "Create or join a game while logged into Slumbr.",
        "Write an answer for each prompt you're given.",
        "Vote for the funnier answer in each head-to-head matchup.",
      ],
      scoring: [
        "Points scale with the share of votes your answer wins.",
        "Win every vote in a matchup for a 'Zingr!' shutout bonus.",
        "Round 2 is the Last Lash — one prompt, everyone answers, points double.",
      ],
    },
    available: true,
    playRoute: "/games/zingr/menu",
  },
  {
    id: "poker",
    name: "Texas Hold'em",
    tagline: "No-limit poker — bluff, raise, and take the pot.",
    description:
      "Full multiplayer Texas Hold'em for up to 9 players. Post the blinds, get your hole cards, and bet across the flop, turn, and river. Custom stakes in private tables, default stakes in public ones. Real-time over Cloudflare Durable Objects with in-game chat (GIFs, stickers & emoji) and slumbr invites.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#1f7a4d] via-[#3a2a18] to-[#1a0f24]",
    icon: "♥",
    screenshots: [],
    rules: {
      goal: "Make the best five-card hand from your two hole cards and five community cards — or bluff everyone else into folding.",
      controls: [
        "Create or join a table while logged into Slumbr.",
        "Fold, check/call, or bet/raise on your turn.",
        "Use the chip slider and pot-size shortcuts to size your bets.",
      ],
      scoring: [
        "Best five-card poker hand at showdown wins the pot.",
        "Side pots are handled automatically for all-ins.",
        "Private tables let the host set blinds, starting chips, and seats.",
      ],
    },
    available: true,
    playRoute: "/games/poker/menu",
  },
  {
    id: "doodlr",
    name: "Doodlr",
    tagline: "Draw the prompt. Bluff your friends. Spot the truth.",
    description:
      "A draw-and-bluff party game for 3-8 players. Everyone doodles weird prompts, then each drawing is shown one at a time — you invent a fake title to fool the room, and vote on which title is the real prompt. Fool people or find the truth to score. Two rounds, double points in the second. Real-time over Cloudflare Durable Objects.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#c6f24e] via-[#3a6a1f] to-[#14130f]",
    icon: "✏️",
    screenshots: [],
    rules: {
      goal: "Score the most points by fooling players with fake titles and spotting the real prompt.",
      controls: [
        "Create or join a game while logged into Slumbr.",
        "Draw your two prompts, then write a convincing fake title for others' drawings.",
        "Vote for the title you think is the real prompt.",
      ],
      scoring: [
        "Pick the real prompt: +1000. The artist scores for every correct guesser.",
        "Fool someone with your fake title: +500 each.",
        "Round 2 is worth double points.",
      ],
    },
    available: true,
    playRoute: "/games/doodlr/menu",
  },
  {
    id: "bluffr",
    name: "Bluffr",
    tagline: "Lie your way through trivia. Spot the truth.",
    description:
      "A bluff-trivia party game for 3-8 players. Everyone sees the same fill-in-the-blank trivia question and invents a fake answer that sounds true. All the lies and the real answer get shuffled together — find the truth, or fool everyone with your lie. Over 1,000 questions, escalating points, and a high-stakes final round. Real-time over Cloudflare Durable Objects.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#22d3ee] via-[#1e3a8a] to-[#0b0e26]",
    icon: "🎭",
    screenshots: [],
    rules: {
      goal: "Score the most points by spotting the real answer and fooling others with your lies.",
      controls: [
        "Create or join a game while logged into Slumbr.",
        "Write a believable fake answer to each trivia question.",
        "Vote for the answer you think is the real one.",
      ],
      scoring: [
        "Find the truth: +1000 points.",
        "Fool a player with your lie: +500 points each.",
        "Points multiply each round — the final question is worth the most.",
      ],
    },
    available: true,
    playRoute: "/games/bluffr/menu",
  },
  {
    id: "villagr",
    name: "Villagr",
    tagline: "RuneScape skilling meets idle village life.",
    description:
      "An isometric skilling sim: command your Villagrs to chop, mine and farm world nodes, training 6 RuneScape-style skills (1–99) into a shared Village Bank — even while you're offline. Each Villagr has a rarity, traits and equipment slots. Share your base with friends as Co-Mayrs. A claimable, procedurally-generated overworld is on the way.",
    category: "Multiplayer",
    coverGradient: "bg-gradient-to-br from-[#caa45a] via-[#5b6e3a] to-[#14110a]",
    icon: "🏕️",
    screenshots: [],
    rules: {
      goal: "Train your Villagrs' skills to 99 and grow a thriving base.",
      controls: [
        "Click a Villagr to inspect their rarity, traits, skills and gear.",
        "Assign each Villagr a skill — they auto-gather it into the Bank.",
        "Share your invite code so friends can Co-Mayr your base.",
      ],
      scoring: [
        "Gathering runs in real time and continues while you're away (capped).",
        "Higher skill levels gather faster; traits add bonuses.",
        "Resources bank automatically — train all six skills toward 99.",
      ],
    },
    available: true,
    playRoute: "/games/villagr",
  },
  {
    id: "dancr",
    name: "Dancr",
    tagline: "Dance to the beat — your camera is the controller.",
    description:
      "A camera-driven dance & rhythm game. Pick a song, follow the routine, and the game scores how well your real-body moves match the choreography using on-device pose tracking. Build your own routines in the editor, climb song leaderboards, and dance with a 3D avatar. Runs as a standalone Slumbr app.",
    category: "Arcade",
    coverGradient: "bg-gradient-to-br from-[#7c3aed] via-[#ec4899] to-[#1a0b2e]",
    icon: "🕺",
    screenshots: [],
    rules: {
      goal: "Match the on-screen choreography with your real body to score points and top the song leaderboard.",
      controls: [
        "Allow camera access — your body movement is the controller.",
        "Pick a song and routine, then follow the dancer.",
        "Use the editor to record and publish your own routines.",
      ],
      scoring: [
        "Points scale with how closely your pose matches each beat.",
        "Per-song leaderboards rank the best dancers.",
        "Routines you publish can be played by everyone.",
      ],
    },
    available: true,
    playRoute: "/games/dancr/play",
    embedUrl: "https://dancr.slumbr.xyz",
  },
  {
    id: "rats",
    name: "Rats",
    tagline: "Turn-based artillery warfare — last rat standing wins.",
    description:
      "A Worms-style turn-based artillery battle. Command a squad of rats across destructible terrain, line up the wind and angle, and lob an arsenal of weapons at your enemies. Local and online multiplayer. Built as a standalone Slumbr app.",
    category: "Arcade",
    coverGradient: "bg-gradient-to-br from-[#6b8e23] via-[#3a4a18] to-[#14130f]",
    icon: "🐀",
    screenshots: [],
    rules: {
      goal: "Wipe out every enemy rat before they wipe out yours.",
      controls: [
        "Move and aim on your turn, then fire before the timer runs out.",
        "Account for angle, power, and wind for every shot.",
        "Use the terrain — and destroy it — to your advantage.",
      ],
      scoring: [
        "Eliminate enemy rats to win the round.",
        "Last team with a rat alive takes the match.",
      ],
    },
    available: true,
    playRoute: "/games/rats/play",
    embedUrl: "https://rats.slumbr.xyz",
  },
];

export const GAME_REGISTRY: GameEntry[] = ENTRIES;

export function findGameById(id: string): GameEntry | undefined {
  return GAME_REGISTRY.find((entry) => entry.id === id);
}
